import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Divider, Grid, Typography, Chip } from '@mui/material';
import { RateReview, Refresh } from '@mui/icons-material';
import { enhancedApi as adminApi } from '@store/generated/admin';
import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { FeedbackMetricCard } from '../components/feedback-metric-card';
import { FeedbackDateRangePicker } from '../components/feedback-date-range-picker';
import { formatRelativeTime } from '../utils/formatting';
import type { FeedbackSessionListItemResponse } from '@store/generated/admin';

const { useGetV2AdminFeedbackSessionsQuery } = adminApi;

// Component to display recent feedback sessions
function RecentFeedbackSessions({
  sessions,
  loading,
}: {
  sessions: FeedbackSessionListItemResponse[];
  loading: boolean;
}) {
  const navigate = useNavigate();

  const getSentimentDisplay = (session: FeedbackSessionListItemResponse) => {
    const sentiment = session.overall_sentiment;
    if (!sentiment) return null;

    const config: Record<
      string,
      { emoji: string; label: string; color: 'success' | 'error' | 'warning' | 'default' | 'info' }
    > = {
      positive: { emoji: '🟢', label: 'Positive', color: 'success' },
      negative: { emoji: '🔴', label: 'Negative', color: 'error' },
      mixed: { emoji: '🟡', label: 'Mixed', color: 'warning' },
      neutral: { emoji: '⚪', label: 'Neutral', color: 'default' },
      comment_only: { emoji: '💬', label: 'Comments', color: 'info' },
    };

    const sentimentConfig = config[sentiment] || config.neutral;
    return (
      <Chip
        icon={<Box>{sentimentConfig.emoji}</Box>}
        label={sentimentConfig.label}
        color={sentimentConfig.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const formatFeedbackDetails = (session: FeedbackSessionListItemResponse) => {
    const parts: string[] = [];

    if (
      session.has_note_feedback &&
      session.note_rating !== null &&
      session.note_rating !== undefined
    ) {
      const emoji = session.note_rating === 0 ? '😊' : session.note_rating === 1 ? '☹️' : '😐';
      parts.push(`${emoji} Note`);
    }

    if (session.has_section_feedback && session.section_event_count) {
      parts.push(
        `${session.section_event_count} Section${session.section_event_count > 1 ? 's' : ''}`,
      );
    }

    return parts.join(' + ') || 'No feedback';
  };

  const handleRowClick = (session: FeedbackSessionListItemResponse) => {
    navigate(`/admin-v2/feedback/sessions?session=${session.feedback_session_id}`);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography align="center" color="text.secondary">
            Loading recent sessions...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (sessions.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography align="center" color="text.secondary">
            No recent feedback sessions found
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: 0 }}>
        <Box>
          {sessions.map((session, index) => (
            <Box
              key={session.feedback_session_id}
              onClick={() => handleRowClick(session)}
              sx={{
                p: 2,
                borderBottom: index < sessions.length - 1 ? 1 : 0,
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
              >
                <Box flex={1} minWidth={200}>
                  <Typography variant="body2" fontWeight="medium">
                    {session.user_name || `User ${session.user_id.substring(0, 8)}...`}
                  </Typography>
                  {session.user_email && (
                    <Typography variant="caption" color="text.secondary">
                      {session.user_email}
                    </Typography>
                  )}
                  {session.organization_name && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {session.organization_name}
                    </Typography>
                  )}
                </Box>

                <Box minWidth={100}>{getSentimentDisplay(session)}</Box>

                <Box minWidth={150}>
                  <Typography variant="body2" fontSize="0.875rem">
                    {formatFeedbackDetails(session)}
                  </Typography>
                </Box>

                <Box minWidth={80}>
                  <Typography variant="body2" fontSize="0.875rem">
                    {session.feedback_event_count} event
                    {session.feedback_event_count !== 1 ? 's' : ''}
                  </Typography>
                </Box>

                <Box minWidth={120}>
                  <Typography variant="caption" color="text.secondary">
                    {formatRelativeTime(session.last_modified_date)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function FeedbackDashboard() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  // Fetch sessions for dashboard metrics (we'll calculate client-side)
  // Note: API limits to 1000 per page, so metrics are calculated from the first 1000 sessions
  const {
    data: sessionsData,
    isLoading,
    error,
    refetch,
  } = useGetV2AdminFeedbackSessionsQuery({
    page: 1,
    perPage: 1000, // API maximum limit
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  });

  const handleDateRangeChange = useCallback((start: string | null, end: string | null) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const handleRefreshAll = useCallback(() => {
    void refetch();
  }, [refetch]);

  const getDateRangeLabel = () => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate).toLocaleDateString();
    const end = new Date(endDate).toLocaleDateString();
    return `${start} - ${end}`;
  };

  // Calculate metrics from sessions data
  const metrics = useMemo(() => {
    if (!sessionsData?.items) {
      return {
        totalSessions: 0,
        totalEvents: 0,
        activeProviders: 0,
        noteFeedbackCount: 0,
        sectionFeedbackCount: 0,
        avgEventsPerSession: 0,
        positiveRatings: 0,
        negativeRatings: 0,
        neutralRatings: 0,
        commentOnlyCount: 0,
        positiveRatio: 0,
        negativeRatio: 0,
        sessionsWithComments: 0,
        totalComments: 0,
      };
    }

    const sessions = sessionsData.items;
    // Use the actual total from API response, not just the returned items count
    const totalSessions = sessionsData.total || sessions.length;
    const totalEvents = sessions.reduce((sum, s) => sum + s.feedback_event_count, 0);
    const uniqueUsers = new Set(sessions.map(s => s.user_id)).size;

    // Count feedback types from the returned sessions
    const noteFeedbackCount = sessions.filter(s => s.has_note_feedback).length;
    const sectionFeedbackCount = sessions.filter(s => s.has_section_feedback).length;

    // Calculate sentiment counts
    const positiveRatings = sessions.reduce((sum, s) => sum + (s.positive_rating_count || 0), 0);
    const negativeRatings = sessions.reduce((sum, s) => sum + (s.negative_rating_count || 0), 0);
    const neutralRatings = sessions.reduce((sum, s) => sum + (s.neutral_rating_count || 0), 0);
    const commentOnlyCount = sessions.filter(s => s.overall_sentiment === 'comment_only').length;

    // Count sessions with comments and total comments
    const sessionsWithComments = sessions.filter(
      s => s.has_note_comment || (s.comment_count && s.comment_count > 0),
    ).length;
    const totalComments = sessions.reduce((sum, s) => sum + (s.comment_count || 0), 0);

    const totalRatings = positiveRatings + negativeRatings + neutralRatings;
    const positiveRatio = totalRatings > 0 ? (positiveRatings / totalRatings) * 100 : 0;
    const negativeRatio = totalRatings > 0 ? (negativeRatings / totalRatings) * 100 : 0;

    const avgEventsPerSession = totalSessions > 0 ? totalEvents / totalSessions : 0;

    return {
      totalSessions,
      totalEvents,
      activeProviders: uniqueUsers,
      noteFeedbackCount,
      sectionFeedbackCount,
      avgEventsPerSession,
      positiveRatings,
      negativeRatings,
      neutralRatings,
      commentOnlyCount,
      positiveRatio,
      negativeRatio,
      sessionsWithComments,
      totalComments,
    };
  }, [sessionsData]);

  // Get the 10 most recent sessions for the table
  const recentSessions = useMemo(() => {
    if (!sessionsData?.items) return [];
    // API returns sessions sorted by most recent first, so just take first 10
    return sessionsData.items.slice(0, 10);
  }, [sessionsData]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        {/* Page Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center" gap={2}>
            <RateReview fontSize="large" color="primary" />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Feedback Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Global overview of user feedback on notes and sections
              </Typography>
            </Box>
          </Box>
          <Button
            startIcon={<Refresh />}
            onClick={handleRefreshAll}
            variant="outlined"
            disabled={isLoading}
          >
            Refresh All
          </Button>
        </Box>

        {/* Date Range Picker */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <FeedbackDateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
              defaultPreset="7days"
            />
            {startDate && endDate && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Selected Range: {getDateRangeLabel()}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Global Metrics Section */}
        <Box mb={6}>
          <Typography variant="h5" fontWeight="medium" mb={3}>
            Global Metrics
          </Typography>

          {error ? (
            <Card>
              <CardContent>
                <Typography color="error" align="center">
                  Failed to load dashboard metrics
                </Typography>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button onClick={() => void refetch()} variant="outlined" size="small">
                    Retry
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : sessionsData && sessionsData.total && sessionsData.total > 1000 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" align="center">
                  There are {sessionsData.total.toLocaleString()} sessions in this date range.
                  Metrics are only calculated for up to 1,000 sessions at a time.
                </Typography>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    onClick={() => navigate('/admin-v2/feedback/sessions')}
                    variant="outlined"
                    size="small"
                  >
                    View All Sessions
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <FeedbackMetricCard
                  title="Total Sessions"
                  value={metrics.totalSessions}
                  subtitle={getDateRangeLabel() || 'Last 7 days'}
                  loading={isLoading}
                  format="number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <FeedbackMetricCard
                  title="Total Events"
                  value={metrics.totalEvents}
                  subtitle="All feedback events"
                  loading={isLoading}
                  format="number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <FeedbackMetricCard
                  title="Active Providers"
                  value={metrics.activeProviders}
                  subtitle="Giving feedback"
                  loading={isLoading}
                  format="number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <FeedbackMetricCard
                  title="Note Feedback"
                  value={metrics.noteFeedbackCount}
                  subtitle="Sessions with note feedback"
                  loading={isLoading}
                  format="number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <FeedbackMetricCard
                  title="Section Feedback"
                  value={metrics.sectionFeedbackCount}
                  subtitle="Sessions with section feedback"
                  loading={isLoading}
                  format="number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <FeedbackMetricCard
                  title="Avg Events/Session"
                  value={metrics.avgEventsPerSession}
                  subtitle="Average per session"
                  loading={isLoading}
                  format="number"
                />
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Sentiment Metrics Section */}
        {sessionsData && sessionsData.total && sessionsData.total <= 1000 && (
          <Box mb={6}>
            <Typography variant="h5" fontWeight="medium" mb={3}>
              Sentiment Analysis
            </Typography>

            {error ? (
              <Card>
                <CardContent>
                  <Typography color="error" align="center">
                    Failed to load sentiment metrics
                  </Typography>
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Button onClick={() => void refetch()} variant="outlined" size="small">
                      Retry
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <FeedbackMetricCard
                    title="Positive Ratings"
                    value={metrics.positiveRatings}
                    subtitle={`${metrics.positiveRatio.toFixed(1)}% of ratings`}
                    loading={isLoading}
                    format="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FeedbackMetricCard
                    title="Negative Ratings"
                    value={metrics.negativeRatings}
                    subtitle={`${metrics.negativeRatio.toFixed(1)}% of ratings`}
                    loading={isLoading}
                    format="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FeedbackMetricCard
                    title="Neutral Ratings"
                    value={metrics.neutralRatings}
                    subtitle="Somewhat satisfied"
                    loading={isLoading}
                    format="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FeedbackMetricCard
                    title="Comment Only"
                    value={metrics.commentOnlyCount}
                    subtitle="Sessions with comments only"
                    loading={isLoading}
                    format="number"
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        )}

        {/* Comments Metrics Section */}
        {sessionsData && sessionsData.total && sessionsData.total <= 1000 && (
          <Box mb={6}>
            <Typography variant="h5" fontWeight="medium" mb={3}>
              Comments Analysis
            </Typography>

            {error ? (
              <Card>
                <CardContent>
                  <Typography color="error" align="center">
                    Failed to load comment metrics
                  </Typography>
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Button onClick={() => void refetch()} variant="outlined" size="small">
                      Retry
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={6}>
                  <FeedbackMetricCard
                    title="Sessions with Comments"
                    value={metrics.sessionsWithComments}
                    subtitle={`${metrics.totalSessions > 0 ? ((metrics.sessionsWithComments / metrics.totalSessions) * 100).toFixed(1) : 0}% of sessions`}
                    loading={isLoading}
                    format="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <FeedbackMetricCard
                    title="Total Comments"
                    value={metrics.totalComments}
                    subtitle="Across all feedback events"
                    loading={isLoading}
                    format="number"
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        )}

        <Divider sx={{ my: 4 }} />

        {/* Recent Sessions Section */}
        <Box mb={6}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h5" fontWeight="medium">
              Most Recent Feedback Sessions
            </Typography>
            <Button
              onClick={() => navigate('/admin-v2/feedback/sessions')}
              variant="outlined"
              size="small"
            >
              View All
            </Button>
          </Box>
          {error ? (
            <Card>
              <CardContent>
                <Typography color="error" align="center">
                  Failed to load recent sessions
                </Typography>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button onClick={() => void refetch()} variant="outlined" size="small">
                    Retry
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <RecentFeedbackSessions sessions={recentSessions} loading={isLoading} />
          )}
        </Box>
      </main>
    </div>
  );
}
